import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../../components/StatusPill'
import { acceptCartridgeDraft, getAiRun, rejectCartridgeDraft } from '../../../api/aiCartridgeDraft'
import { ApiError } from '../../../api/problem'
import { AiDraftCitationList } from './AiDraftCitationList'
import { AiDraftDiffView } from './AiDraftDiffView'
import type { AiCartridgeDraftProposalView, AiDraftKind, AiRunView } from '../../../types/aiCartridgeDraft'

export interface AiDraftProposalCardProps {
  programId: string
  proposal: AiCartridgeDraftProposalView
  onAccepted: (updated: AiCartridgeDraftProposalView) => void
  onRejected: (updated: AiCartridgeDraftProposalView) => void
}

const DRAFT_KIND_LABEL: Record<AiDraftKind, string> = {
  rule_set: 'RuleSet',
  parameter: 'Parameter',
  eligibility_model: 'EligibilityModel decision table',
}

const DEEP_LINK_TAB: Record<AiDraftKind, string> = {
  rule_set: 'parameters',
  parameter: 'parameters',
  eligibility_model: 'eligibility',
}

function statusVariant(status: AiCartridgeDraftProposalView['status']): StatusPillVariant {
  if (status === 'accepted') return 'success'
  if (status === 'rejected') return 'danger'
  return 'info'
}

function statusLabel(status: AiCartridgeDraftProposalView['status']): string {
  if (status === 'accepted') return 'Accepted'
  if (status === 'rejected') return 'Rejected'
  return 'Pending review'
}

/**
 * The design-system §3.4 `AIReviewCard` contract made concrete for a cartridge-draft target (the
 * FIRST such build — no `AIReviewCard` component existed before this story; the Tech Lead ruling in
 * `docs/design/ib-287-ai-draft-ui-design.md` §6 makes this the reference implementation).
 *
 * <b>Disclosed contract gap — no "Edit" action (DoR AC6 not implementable against the merged
 * backend).</b> The design/DoR both describe an Edit step that makes `content` editable inline and
 * resubmits it as `{comment, edits}` on accept. The MERGED `CartridgeDraftProposalService.accept` /
 * `DecideProposalRequest` (`ai.certainti.incentiwise.module.ai.internal.web`) carries `{comment}`
 * ONLY — there is no `edits` field and no server-side path that would ever apply an edited payload.
 * Building an "Edit" control with nowhere to send its result would be a dead button (CLAUDE.md §4
 * "every CTA must open a real page or a defined action" / "no dead buttons") — so this card offers
 * only the two actions the real endpoint supports: **Accept** (with an optional review comment) and
 * **Reject**. This is named here, in the DoR-facing hand-off note, and in the PR description as a
 * backend-contract gap for IB-286 to close in a fast-follow, not silently dropped.
 */
export function AiDraftProposalCard({ programId, proposal, onAccepted, onRejected }: AiDraftProposalCardProps) {
  const [aiRun, setAiRun] = useState<AiRunView | null>(null)
  const [aiRunError, setAiRunError] = useState<string | null>(null)
  const [decideAction, setDecideAction] = useState<'accept' | 'reject' | null>(null)
  const [comment, setComment] = useState('')
  const [deciding, setDeciding] = useState(false)
  const [decideError, setDecideError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setAiRun(null)
    setAiRunError(null)
    getAiRun(proposal.aiRunId, controller.signal)
      .then((run) => {
        if (!controller.signal.aborted) setAiRun(run)
      })
      .catch(() => {
        if (controller.signal.aborted) return
        // Best-effort enrichment only — citations/honest-stub disclosure may be incomplete, but the
        // rest of the card (rationale, diff, Accept/Reject) must still render and work.
        setAiRunError('Could not load this draft’s run details (citations/honest-stub disclosure may be incomplete).')
      })
    return () => controller.abort()
  }, [proposal.aiRunId])

  function openDecide(action: 'accept' | 'reject') {
    setDecideAction(action)
    setComment('')
    setDecideError(null)
  }

  function closeDecide() {
    setDecideAction(null)
    setComment('')
    setDecideError(null)
  }

  async function confirmDecide() {
    if (!decideAction || deciding) return
    setDeciding(true)
    setDecideError(null)
    try {
      const updated =
        decideAction === 'accept'
          ? await acceptCartridgeDraft(proposal.id, comment)
          : await rejectCartridgeDraft(proposal.id, comment)
      closeDecide()
      if (decideAction === 'accept') onAccepted(updated)
      else onRejected(updated)
    } catch (err) {
      setDecideError(friendlyAiCartridgeDraftError(err, 'Could not record that decision.'))
    } finally {
      setDeciding(false)
    }
  }

  return (
    <div
      role="group"
      aria-label={`AI draft proposal — ${DRAFT_KIND_LABEL[proposal.draftKind]}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--iw-space-3)',
        padding: 'var(--iw-space-4)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius-md)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="sparkles" size={16} />
        </span>
        <strong style={{ fontSize: 'var(--iw-size-sm)' }}>{DRAFT_KIND_LABEL[proposal.draftKind]}</strong>
        <StatusPill variant={statusVariant(proposal.status)} label={statusLabel(proposal.status)} />
        {proposal.confidence !== null ? (
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
            confidence {proposal.confidence}%
          </span>
        ) : null}
      </div>

      <InlineAlert severity="info">
        {proposal.status === 'pending'
          ? 'AI-assisted — advisory, not yet human-reviewed.'
          : proposal.status === 'accepted'
            ? 'AI-assisted content, accepted into a new draft. It is flagged ai_generated and must still travel the full draft → review → approve → publish path behind a distinct approver (maker ≠ checker) — this card never submits, approves, or publishes it.'
            : 'AI-assisted content — rejected. No canon draft was created.'}
      </InlineAlert>

      {aiRun?.isStubProduced ? (
        <InlineAlert severity="info">
          AI-assisted draft, produced by a deterministic template (not a live model). Review every rule and
          citation before accepting.
        </InlineAlert>
      ) : null}
      {aiRunError ? <InlineAlert severity="error">{aiRunError}</InlineAlert> : null}

      <p style={{ margin: 0, fontSize: 'var(--iw-size-sm)' }}>{proposal.rationale}</p>

      <div>
        <h4 style={{ margin: '0 0 var(--iw-space-1)', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
          Citations
        </h4>
        <AiDraftCitationList citations={aiRun?.provenance ?? []} />
      </div>

      <div>
        <h4 style={{ margin: '0 0 var(--iw-space-1)', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
          Proposed vs current
        </h4>
        <AiDraftDiffView programId={programId} draftKind={proposal.draftKind} content={proposal.content} />
      </div>

      {proposal.status === 'accepted' && proposal.resultId ? (
        <Link to={`/admin/cartridge-studio/programs/${programId}?tab=${DEEP_LINK_TAB[proposal.draftKind]}`}>
          Open the created draft in {DEEP_LINK_TAB[proposal.draftKind] === 'eligibility' ? 'Eligibility' : 'RuleSets / Parameters'}
        </Link>
      ) : null}

      {proposal.status === 'pending' ? (
        decideAction ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
            {decideError ? <InlineAlert severity="error">{decideError}</InlineAlert> : null}
            <label className="iw-field-label" htmlFor="ai-draft-decide-comment">
              {decideAction === 'accept' ? 'Optional comment to accompany accepting this draft' : 'Optional comment on why this draft is being rejected'}
            </label>
            <textarea
              id="ai-draft-decide-comment"
              className="iw-input"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <p style={{ margin: 0, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
              There is no edit-before-accept step in this release — review the content above, then confirm.
            </p>
            <div style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
              <button
                type="button"
                className="iw-btn iw-btn--sm iw-btn--primary"
                onClick={() => void confirmDecide()}
                disabled={deciding}
              >
                {deciding ? 'Saving…' : decideAction === 'accept' ? 'Confirm accept' : 'Confirm reject'}
              </button>
              <button type="button" className="iw-btn iw-btn--sm" onClick={closeDecide} disabled={deciding}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
            <button type="button" className="iw-btn iw-btn--sm iw-btn--primary" onClick={() => openDecide('accept')}>
              <Icon name="check" size={14} /> Accept
            </button>
            <button type="button" className="iw-btn iw-btn--sm" onClick={() => openDecide('reject')}>
              <Icon name="x" size={14} /> Reject
            </button>
          </div>
        )
      ) : null}
    </div>
  )
}

/** Every failure mode named in the design doc §5: 403 (capability denial), 404 (proposal/program not
 * found), 409/422 (already decided / validation on the accept-create path). */
export function friendlyAiCartridgeDraftError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const slug = err.problem.type?.split('/').pop()
    const detail = err.problem.detail
    if (err.status === 403 || slug === 'capability-denied') {
      return detail
        ? `AI-assisted drafting is a platform-only capability. ${detail}`
        : 'AI-assisted drafting is a platform-only capability — your current session cannot perform it.'
    }
    if (err.status === 404 || slug === 'not-found') {
      return detail ?? 'This AI draft (or its Program) could not be found.'
    }
    if (err.status === 409 || slug === 'proposal_already_decided') {
      return detail ?? 'This draft has already been accepted or rejected.'
    }
    if (err.status === 422) {
      return detail ?? 'This draft could not be accepted — the drafted content failed validation.'
    }
    return detail ?? err.message
  }
  return fallback
}
